import { ArrayDataSource } from '@angular/cdk/collections';
import { ElementRef, Injectable, signal } from '@angular/core';
import { AccountDto, MessageManagementModel } from '@ay-gosu/server-shared';
import { asyncJob } from '@ay-gosu/ui/common/async-job';
import { MatConnectedDialog } from '@ay-gosu/ui/common/connected-dialog';
import { Card } from '@ay/bot';
import { firstValueFrom } from 'rxjs';
import { GosuValidate } from '../components/gosu-validator/gosu-validator.class';
import { MessageDialog } from '../dialog/message/message.dialog';
import { CardsMessage } from '../message/cards/cards.message';
import { FlexMessage } from '../message/flex/flex.message';
import { PackageFactoryService } from '../message/package-factory.service';
import { Package } from '../message/package.class';
import { PosterMessage } from '../message/poster/poster.message';
import { CreateFolderComponent } from '../pages/message-management/create-folder/create-folder.component';
import { TokenService } from './token.service';

@Injectable({ providedIn: 'root' })
export class MessageManagementService {
  public readonly folderId = signal<number>(null);

  public folderList$ = asyncJob(
    (account: AccountDto) => MessageManagementModel.fetchFolders(),
    this._tokenService.account$,
  );

  public readonly folder$ = asyncJob(
    (folders, folderId) => folders.find((folder) => folder.id === folderId),
    this.folderList$,
    this.folderId,
  );

  public readonly messageId = signal<number>(null);

  public messageList$ = asyncJob(
    (folder) => folder?.messages ?? [],
    this.folder$,
  );

  public readonly message$ = asyncJob(
    (messages, messageId) =>
      messages.find((message) => message.id === messageId) ?? null,
    this.messageList$,
    this.messageId,
  );

  public readonly selectShareMessagesTree$ = asyncJob(
    (folders) => [
      {
        id: -1,
        name: '分享這則訊息',
        type: 'self',
        hasChildren: () => false,
        getChildren: () => new ArrayDataSource([]),
      },
      ...folders.map((folder) => ({
        id: folder.id,
        name: folder.name,
        type: 'folder',
        hasChildren: () => true,
        getChildren: () =>
          new ArrayDataSource([
            ...folder.messages
              .filter((message) => message.id !== this.messageId())
              .map((message) => ({ ...message, type: 'message' })),
            {
              id: -1,
              name: '建立新訊息',
              type: 'create-message',
              folderId: folder.id,
            },
          ]),
      })),
      {
        id: -1,
        name: '建立訊息資料夾',
        type: 'create-folder',
        hasChildren: () => false,
        getChildren: () => new ArrayDataSource([]),
      },
    ],
    this.folderList$,
  );

  public readonly selectAddFriendThroughShareMessagesTree$ = asyncJob(
    (folders) => [
      ...folders.map((folder) => ({
        id: folder.id,
        name: folder.name,
        type: 'folder',
        hasChildren: () => true,
        getChildren: () =>
          new ArrayDataSource([
            ...folder.messages
              .filter((message) => message.id !== this.messageId())
              .map((message) => ({ ...message, type: 'message' })),
            {
              id: -1,
              name: '建立新訊息',
              type: 'create-message',
              folderId: folder.id,
            },
          ]),
      })),
      {
        id: -1,
        name: '建立訊息資料夾',
        type: 'create-folder',
        hasChildren: () => false,
        getChildren: () => new ArrayDataSource([]),
      },
    ],
    this.folderList$,
  );

  public constructor(
    private readonly _matConnectedDialog: MatConnectedDialog,
    private readonly _tokenService: TokenService,
    private readonly _packageFactoryService: PackageFactoryService,
  ) {}

  public async createFolder(elementRef?: ElementRef) {
    const dialogRef = this._matConnectedDialog.open(CreateFolderComponent, {
      elementRef,
    });

    const res = await firstValueFrom(dialogRef.afterClosed());
    return res;
  }

  public async createMessage(
    folderId: number,
    type: 'message' | 'share-message' | 'share-add-friend',
    callback?: (result: any) => void,
  ) {
    const accountId = await firstValueFrom(this._tokenService.rxAccount$).then(
      (account) => account.accountId,
    );

    const dialogRef = this._matConnectedDialog.open(MessageDialog, {
      disableClose: true,
      data: { accountId, folderId, type },
    });

    dialogRef.componentInstance.package =
      await this._packageFactoryService.createForMessageManagement();

    dialogRef.afterClosed().subscribe((result) => {
      callback?.(result);
    });
  }

  public async editMessage(
    folderId: number,
    messageId: number,
    callback?: (result: any) => void,
    type?: 'message' | 'share-message' | 'share-add-friend',
    isEdit?: boolean,
  ) {
    const accountId = await firstValueFrom(this._tokenService.rxAccount$).then(
      (account) => account.accountId,
    );

    const dialogRef = this._matConnectedDialog.open(MessageDialog, {
      disableClose: true,
      data: { accountId, folderId, messageId, isEdit, type },
    });

    dialogRef.afterClosed().subscribe((result) => {
      callback?.(result);
    });
  }

  public async fetchMessageById(messageId: number) {
    const folders = await this.folderList$.toPromise();
    let message = null;

    for (const folder of folders) {
      message = folder.messages.find((message) => message.id === messageId);
      if (message) {
        break;
      }
    }

    return message;
  }

  public checkError(pkg: Package) {
    let hasError = false;

    pkg.messages.forEach((message) => {
      if (message instanceof CardsMessage) {
        hasError = this._checkCardMessage(message);
      }

      if (message instanceof PosterMessage) {
        hasError = this._checkPosterMessage(message);
      }

      if (message instanceof FlexMessage) {
        hasError = this._checkFlexMessage(message);
      }
    });

    return hasError;
  }

  private _checkCardMessage(message: CardsMessage) {
    let hasError = false;

    message.content.cards.forEach((card) => {
      if (!(card instanceof Card)) {
        return;
      }
      card.buttons.forEach((button) => {
        if (
          button.type === 'share-message' &&
          GosuValidate.required(button.messageId) &&
          !button.shareCurrentMessage
        ) {
          hasError = true;
        }
        if (
          button.type === 'share-add-friend' &&
          GosuValidate.required(button.messageId) &&
          !button.shareCurrentMessage
        ) {
          hasError = true;
        }
      });
    });

    return hasError;
  }

  private _checkPosterMessage(message: PosterMessage) {
    let hasError = false;

    message.content.areas.forEach((area) => {
      if (area.type === 'share-message' || area.type === 'share-add-friend') {
        if (
          GosuValidate.required(area.messageId) &&
          !area.shareCurrentMessage
        ) {
          hasError = true;
        }
      }
    });

    return hasError;
  }

  private _checkFlexMessage(message: FlexMessage) {
    let hasError = false;

    const actions = this._findActions(message.content);
    actions.forEach((action) => {
      if (
        action.type === 'share-message' ||
        action.type === 'share-add-friend'
      ) {
        if (
          GosuValidate.required(action.messageId) &&
          !action.shareCurrentMessage
        ) {
          hasError = true;
        }
      }
    });

    return hasError;
  }

  private _findActions(content: any) {
    const actions = [];

    function traverse(node) {
      if (typeof node !== 'object' || node === null) return;

      if (node.action && node.action.type) {
        actions.push(node.action);
      }

      for (const key in node) {
        if (node.hasOwnProperty(key)) {
          traverse(node[key]);
        }
      }
    }

    traverse(content);

    return actions;
  }
}
